/**
 * WHAT THE CLIENT LOOKED LIKE WHEN SOMETHING BROKE — the small, shared facts a fault report needs
 * to be attributable, gathered only on a failure path so a healthy page pays nothing.
 *
 * Kept separate from the probes that use it so there is ONE definition of «which engine is this»
 * rather than a copy per beacon, and so `ErrorBoundary` does not have to import the style sentinel
 * to ask a question that has nothing to do with stylesheets.
 */

/**
 * The rendering engine, recorded rather than inferred from anything downstream. iOS is WebKit
 * whatever the browser badge says — every iOS browser is a UIWebView/WKWebView underneath, which
 * is why the badge is useless and the platform check comes first.
 */
export function engineTag(): string {
  try {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return "webkit"; // every iOS browser is WebKit underneath
    if (/Firefox\/|FxiOS\//.test(ua)) return "gecko";
    if (/Chrom(e|ium)\/|CriOS\/|Edg\//.test(ua)) return "blink";
    if (/AppleWebKit\//.test(ua)) return "webkit";
    return "other";
  } catch {
    return "?";
  }
}

/**
 * Engine + a coarse platform, e.g. `webkit/ios`, `blink/android`, `gecko`.
 *
 * ⚠️ DELIBERATELY LOW-CARDINALITY, AND THAT IS NOT A STYLE PREFERENCE. `fingerprintOf` hashes the
 * first 200 characters of the report message, so a high-cardinality field that lands inside that
 * window splits ONE fault into a row per value — a raw user agent would produce a new ErrorLog row
 * per device and per browser build. A handful of engine/platform buckets cannot, and the split it
 * does allow (iOS vs Android) is a distinction worth having: `style-broken` turned out to be two
 * different faults wearing one shape, told apart by exactly this.
 *
 * The raw UA is also nearly redundant for the crash it was proposed for — the DOM `NotFoundError`
 * already reads differently per engine («Failed to execute 'removeChild' on 'Node'» vs «The object
 * can not be found here»), so the engine was legible from the message alone.
 */
export function clientTag(): string {
  const engine = engineTag();
  try {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return `${engine}/ios`;
    if (/Android/.test(ua)) return `${engine}/android`;
    return engine;
  } catch {
    return engine;
  }
}

/**
 * IS THE PAGE BEING TRANSLATED RIGHT NOW? — `yes` | `no` | `?`.
 *
 * The standing hypothesis for the recurring `render-crash` in the حکم online waiting room is a DOM
 * mutation from OUTSIDE React: a browser/OS translation rewriting text nodes underneath a subtree
 * React then tries to reconcile. That is the classic cause of a DOM `NotFoundError`, and nothing in
 * that subtree uses portals, `innerHTML` or direct node removal.
 *
 * `<font>` is the probe because it is UNAMBIGUOUS IN THIS APP: neither the source nor the shipped
 * bundle and stylesheet contain a single `<font>` (measured 2026-08-06), so any `<font>` in the
 * live DOM was put there by something else — which is precisely what both Chrome's and Safari's
 * translation do to text. Chrome additionally stamps `translated-ltr` / `translated-rtl` on
 * `<html>`, so that is checked too; either one is enough.
 *
 * ⚠️ THIS READS THE AFTERMATH, NOT THE MOMENT. It runs in `componentDidCatch`, after the failure.
 * Translation-inserted nodes persist, so the reading is reliable — but `yes` means «translation was
 * active on a page that crashed», which is a strong lead given the error class, NEVER a proof of
 * cause. Do not let it harden into one.
 */
export function translatedTag(doc: Document = document): string {
  try {
    if (doc.querySelectorAll("font").length > 0) return "yes";
    const cls = doc.documentElement?.className ?? "";
    return /\btranslated-(ltr|rtl)\b/.test(cls) ? "yes" : "no";
  } catch {
    // A probe on a failure path must never become the failure it is describing.
    return "?";
  }
}
